// Public CMS reader. Hydrates blog posts and case studies created in the admin
// (`/admin`) into the public listing/detail pages — alongside hardcoded content.

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function fetchPublicBlog() {
  try {
    const res = await axios.get(`${API}/blog`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function fetchPublicBlogBySlug(slug) {
  try {
    const res = await axios.get(`${API}/blog/${encodeURIComponent(slug)}`);
    return res.data || null;
  } catch {
    return null;
  }
}

export async function fetchPublicCases() {
  try {
    const res = await axios.get(`${API}/case-studies`);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function fetchPublicCaseBySlug(slug) {
  try {
    const res = await axios.get(`${API}/case-studies/${encodeURIComponent(slug)}`);
    return res.data || null;
  } catch {
    return null;
  }
}

// Resolve relative /api/uploads URLs (from CMS image upload) against the
// backend origin so public pages render correctly regardless of environment.
function resolveImageUrl(url, fallback) {
  if (!url) return fallback;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

const ACCENTS = ["#FFB000", "#3BA7D1", "#0FB5A6", "#E96A7F", "#6B77E8", "#B7D455"];

export function pickAccent(seed) {
  if (!seed) return ACCENTS[0];
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) & 0xfffffff;
  }
  return ACCENTS[h % ACCENTS.length];
}

// Compute reading time from a CMS body — handles both raw text and HTML.
// 220 WPM is the widely-cited average for English non-fiction reading,
// rounded up to the nearest minute. Falls back to "5 min" if body is empty.
function computeReadTime(rawBody) {
  if (!rawBody || typeof rawBody !== "string") return "5 min";
  const text = rawBody
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&[a-z0-9#]+;/gi, " ")
    .trim();
  if (!text) return "5 min";
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 220));
  return `${minutes} min`;
}

// Map a CMS blog post (server schema) → frontend listing card shape.
export function mapBlogToCard(p) {
  const created = p.created_at ? new Date(p.created_at) : null;
  const date = created
    ? created.toLocaleDateString("en-IN", { month: "short", year: "numeric" })
    : "";
  return {
    id: p.slug,
    slug: p.slug,
    topic: p.topic || "Insight",
    date,
    readTime: p.read_time || computeReadTime(p.body),
    title: p.title,
    excerpt: p.excerpt || "",
    img: resolveImageUrl(
      p.cover_image,
      "https://images.unsplash.com/photo-1455390582262-044cdead277a?q=80&w=1600&auto=format&fit=crop"
    ),
    accent: pickAccent(p.slug || p.title),
    cms: true,
    body: p.body,
    author: p.author,
    featured: !!p.featured,
  };
}

export function mapCaseToCard(c) {
  return {
    id: c.slug,
    slug: c.slug,
    cat: c.category || "Case Study",
    tag: c.client || c.category || "",
    title: c.title,
    subtitle: c.subtitle || "",
    stat: c.stat || "—",
    statLabel: c.stat_label || "",
    accent: pickAccent(c.slug || c.title),
    img: resolveImageUrl(
      c.cover_image,
      "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2000&auto=format&fit=crop"
    ),
    cms: true,
    challenge: c.challenge,
    approach: c.approach,
    outcomes: c.outcomes,
    region: c.region,
    duration: c.duration,
    services: c.services,
  };
}
